'use client'

import { useCallback } from 'react'
import {
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'

import { useClientApi } from '../useClientApi'

import type {
  TAddBookmarksInput,
  TAddBookmarksOutput,
  TDeleteBookmarksInput,
  TDeleteBookmarksOutput,
  TGetBookmarksOutput,
  TVesselJourneyResponse
} from '@/api/v1/resources/vesselJourney'

export type TOptionsQueryVesselJourneyBookmarks = UseQueryOptions<
  TGetBookmarksOutput,
  unknown,
  TGetBookmarksOutput,
  QueryKey
>

const getQueryKey = (): QueryKey => ['v1', 'vesselJourney', 'bookmarks']

const useQueryVesselJourneyBookmarks = (
  options?: Partial<TOptionsQueryVesselJourneyBookmarks> & { enabled?: boolean }
) => {
  const { api, userInitialized } = useClientApi()
  const { enabled = true, ...queryOptions } = options || {}

  return useQuery({
    queryKey: getQueryKey(),
    queryFn: async () => {
      return api.vesselJourney.getBookmarks()
    },
    enabled: !!userInitialized && enabled,
    staleTime: 1 * 60 * 1000, // 1 minute - bookmarks are considered fresh for 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes - keep bookmarks in cache for 5 minutes
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnMount: false, // Don't refetch on component mount if data exists
    ...queryOptions
  })
}

export const useMutationAddVesselJourneyBookmarks = (
  options?: UseMutationOptions<TAddBookmarksOutput, unknown, TAddBookmarksInput>
) => {
  const { api, userInitialized } = useClientApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: TAddBookmarksInput) => {
      if (!userInitialized) throw new Error('Not authenticated')
      return api.vesselJourney.addBookmarks(input)
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: getQueryKey() })
      options?.onSuccess?.(...args)
    },
    ...options
  })
}

const useMutationDeleteVesselJourneyBookmarks = (
  options?: UseMutationOptions<TDeleteBookmarksOutput, unknown, TDeleteBookmarksInput>
) => {
  const { api, userInitialized } = useClientApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: TDeleteBookmarksInput) => {
      if (!userInitialized) throw new Error('Not authenticated')
      return api.vesselJourney.deleteBookmarks(input)
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: getQueryKey() })
      options?.onSuccess?.(...args)
    },
    ...options
  })
}

export default function useVesselJourneyBookmarks(
  options?: Partial<TOptionsQueryVesselJourneyBookmarks> & { enabled?: boolean }
) {
  const queryClient = useQueryClient()

  const queryHook = useQueryVesselJourneyBookmarks(options)

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: getQueryKey() })
  }, [queryClient])

  const addMutation = useMutationAddVesselJourneyBookmarks()
  const deleteMutation = useMutationDeleteVesselJourneyBookmarks()

  const addBookmarkNow = (v: TVesselJourneyResponse) => {
    addMutation.mutate({ imos: [v.ship.imo], returnFinalSubscriptionList: false })
  }

  const removeBookmarkNow = (v: TVesselJourneyResponse) => {
    deleteMutation.mutate({ imos: [v.ship.imo], returnFinalSubscriptionList: false })
  }

  return {
    // query
    ...queryHook,
    invalidate,

    // mutations
    post: addMutation,
    delete: deleteMutation,

    bookmarks: queryHook.data,
    loading: queryHook.isLoading,
    addBookmarkNow,
    removeBookmarkNow
  }
}
